import React, { useEffect, useRef } from 'react';
import { formatNumber } from '@/utils/number';
import useCoverageStats from '@/hooks/useCoverageStats';
import Card from '@/components/ui/shared/Card';
import { H4, H5, P } from '../shadcn/typography';
import { cn } from '@/utils/styles';

export enum StatType {
  GlobalCoverage = 'globalCoverage',
  TotalKmMapped = 'totalKmMapped',
  TotalUniqueKm = 'totalUniqueKm',
}

export interface Stats {
  [StatType.GlobalCoverage]: number;
  [StatType.TotalKmMapped]: number;
  [StatType.TotalUniqueKm]: number;
}

const statsTitles = {
  [StatType.TotalKmMapped]: 'Total KM Mapped',
  [StatType.GlobalCoverage]: 'Global Road Coverage',
  [StatType.TotalUniqueKm]: 'Unique KM Mapped',
};

interface GrowingStatsProps {
  className?: string;
}

interface StatBoxProps {
  stat: StatType;
  value: number;
  className?: string;
  textClassName?: string;
  valueClassName?: string;
}

const incrementValues = {
  [StatType.GlobalCoverage]: 1, // Adjust increment for smoother animation
  [StatType.TotalKmMapped]: 10000000, // Adjust increment for smoother animation
  [StatType.TotalUniqueKm]: 1000000, // Adjust increment for smoother animation
};

export const StatBox = ({
  stat,
  textClassName,
  valueClassName,
  className,
  value,
}: StatBoxProps) => {
  const odometerContainer = useRef<HTMLDivElement>(null);
  const spanClassName =
    'md:!text-[40px] !text-[20px] font-monserrat font-semibold !leading-none text-neutral-0';

  const appendValues = (value: number) => {
    odometerContainer.current?.replaceChildren();
    const elementAmount = value / incrementValues[stat];
    for (let i = elementAmount; i > 0; i--) {
      const span = document.createElement('span');
      const currValue = Math.min(incrementValues[stat] * i, value);
      span.textContent = getStatValue(stat, currValue);
      span.className = spanClassName;
      odometerContainer.current?.appendChild(span);
    }
  };

  const animateOdometer = () => {
    if (!odometerContainer.current) {
      return;
    }
    odometerContainer.current.animate(
      [
        {
          transform: 'translateY(-100%)',
        },
        {
          transform: `translateY(0%)`,
        },
      ],
      {
        duration: 3000,
        fill: 'forwards',
        easing: 'cubic-bezier(0.215, 0.610, 0.355, 1.000)',
      },
    );
  };

  useEffect(() => {
    if (value > 0) {
      appendValues(Number(value));
      animateOdometer();
    }
  }, [value]);

  return (
    <Card
      as="article"
      className={cn(
        'flex w-full flex-col items-center justify-center gap-3 rounded-lg border border-neutral-800 bg-neutral-1000 p-4 md:gap-4 lg:px-10 lg:py-6',
        className,
      )}>
      <div
        className={cn(
          'flex h-[20px] flex-col items-center overflow-hidden md:h-[40px]',
          valueClassName,
        )}>
        <div
          ref={odometerContainer}
          className="flex flex-col items-center gap-2 transition-transform duration-700">
          <span className={spanClassName}>{getStatValue(stat, 0)}</span>
        </div>
      </div>
      <P
        className={cn(
          'text-left text-sm font-normal leading-[18px] text-neutral-300 md:w-[80px] lg:w-full lg:text-center lg:text-sm lg:font-normal lg:leading-none',
          textClassName,
        )}>
        {statsTitles[stat as keyof typeof statsTitles]}
      </P>
    </Card>
  );
};

const getStatValue = (stat: StatType, value: number) => {
  let formattedValue = formatNumber(value);
  if (stat === StatType.GlobalCoverage) {
    formattedValue = value + '%';
  }
  return String(formattedValue);
};

const GrowingStats: React.FC<GrowingStatsProps> = () => {
  const { data: stats } = useCoverageStats();

  return (
    <div className="w-full">
      <ul className="flex w-full flex-col items-stretch justify-center gap-3 lg:flex-row lg:gap-6">
        {Object.entries(stats).map(([key, value]) => (
          <li key={key} className="flex w-full">
            <StatBox stat={key as StatType} value={value} />
          </li>
        ))}
      </ul>
    </div>
  );
};

export default GrowingStats;
